import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { API_URL, MESSAGE, message } from 'constant';
import {
	ThemeMode,
	kycSettingsState,
	loginState,
	themeModeState,
} from 'global-stores';
import { useNetwork, useNotification, useTimezoneDate } from 'hooks';
import { validURLLocal as validURL } from 'utils';
import {
	Blank,
	Field,
	Modal,
	PageHead,
	Switch,
	setSv2Theme,
} from 'views/design-system';
import { AddEmailClient } from 'views/email-client';
import { PERMISSION_SCREEN, useUserRoles } from 'views/routes-children';
import { useDashboradhook } from 'views/routes-children/hooks/hook';

import {
	BackUpCodes,
	CompanyDetails,
	EncrptionKeySection,
	GenrateMultifactorAuth,
	LoadingAnimation,
	MFA_ACTIVE_MODAL,
	MFA_MESSAGES,
	MFA_MODAL_HEADING_MESSAGES,
	SubAccountDetails,
	TimezoneSettings,
} from '../general-settings/components';
import { useLoadingAnimation } from '../stores';

import 'views/design-system/tokens.scss';
import 'views/design-system/surface.scss';
/*
 * The embedded cards' stylesheet. CompanyDetails and EncrptionKeySection ship
 * NO scss of their own — every rule they need (the 72px logo box, the round
 * icon badge, the 16px row padding) lives here, and their inner selectors are
 * NESTED under `.company-details-container`, so both the import AND the
 * wrapper class below are required. Imported after surface.scss so the deck's
 * own scss can still neutralise the card chrome `.panel` now provides.
 */
import '../general-settings/general-settings.scss';
import 'views/design-system/legacy-dark.scss';
import './general-settings-deck.scss';

const THEME_OPTIONS: { key: ThemeMode; label: string }[] = [
	{ key: 'light', label: 'Light' },
	{ key: 'dark', label: 'Dark' },
	{ key: 'system', label: 'System' },
];

/**
 * General Settings on the v2 design system.
 *
 * Two kinds of card share the grid. What the old page implemented ITSELF —
 * the whitelisted-domains form, the three feature toggles, the MFA setup
 * flow, appearance — is rebuilt natively on the primitives, calling the same
 * endpoints with the same payloads and messages. The self-contained cards
 * (company details and its edit modal, encryption key, email client,
 * timezone, sub-account details) are the old page's own components mounted
 * unchanged inside sv2 panels: their behaviour is v1's by construction, and
 * the dark pass reaches them through the `--color-*` remap on the deck scope.
 * The legacy `general-settings.tsx` is untouched and still exported.
 */
export const GeneralSettingsDeck = () => {
	const [kycSettings, setKycSettings] = useRecoilState(kycSettingsState);
	const {
		whitelistedDomains,
		_id: clientsDetailid,
		whitelabel,
		showPricing,
	} = kycSettings ?? {};

	const login = useRecoilValue(loginState);
	const { mfaEnabled, parentBusinessId, userId } = login ?? {};
	const [themeMode, setThemeMode] = useRecoilState(themeModeState);

	/**
	 * The panel has TWO theme systems and this card must drive both.
	 * `themeModeState` writes <html data-theme> — every legacy screen follows
	 * it, but nothing sv2 does. The redesigned surfaces read
	 * <html data-sv2-theme> via useSv2Theme. Setting only the atom is the bug
	 * where picking Light here changed nothing visible on this very page:
	 * the page you are standing on is sv2. "System" resolves against the OS
	 * at selection time, since sv2 has exactly two renderings.
	 */
	const applyThemeMode = useCallback(
		(key: ThemeMode) => {
			setThemeMode(key);
			const dark =
				key === 'dark' ||
				(key === 'system' &&
					window.matchMedia('(prefers-color-scheme: dark)').matches);
			setSv2Theme(dark ? 'dark' : 'light');
		},
		[setThemeMode]
	);

	/* ---- domains ---- */
	const [domainInput, setDomainInput] = useState('');
	const [isInputVisible, setInputVisible] = useState(false);
	const [inputErrorMessage, setInputErrorMessage] = useState('');
	const [deleteID, setDeleteId] = useState('');

	/* ---- mfa ---- */
	const [multiFactorModal, setMultifactorModal] = useState(false);
	const [mfaConfirmationModal, setMfaIsConfirmationModal] = useState(false);
	const [mfaEnable, setMfaEnabled] = useState(mfaEnabled);
	const [activeStepName, setActiveStepName] = useState<string>(
		MFA_ACTIVE_MODAL.RGISTER_MFA
	);
	const [verificationCodeValue, setVerificationCodeValue] = useState('');

	const { post: addDomain, data: domainData, loading } = useNetwork();
	const { post: updateWhitelable } = useNetwork();
	const { post: updateShowPricing } = useNetwork();
	const { remove: removeDomain } = useNetwork();
	const { errorNotification, successNotification } = useNotification();
	const { getPipelineInit } = useDashboradhook();
	const { post, data: mfa_setup_data } = useNetwork();
	const { post: mfa_Verify, loading: mfa_verify_loading } = useNetwork();
	const {
		post: mfa_back_Codes,
		data: mfa_back_up_codes,
		loading: back_up_loading,
	} = useNetwork();

	const { checkUserWritePermission } = useUserRoles();
	const { updateStorage } = useLoadingAnimation();
	const { timezone } = useTimezoneDate();

	const canWrite = useMemo(
		() => checkUserWritePermission(PERMISSION_SCREEN.GeneralSettings),
		[checkUserWritePermission]
	);

	/* ------------------------------------------------- domains (v1 logic) */

	useEffect(() => {
		if (domainData) {
			if (domainData.whitelistedDomains) {
				setKycSettings(prev => ({
					...prev,
					whitelistedDomains: domainData.whitelistedDomains,
				}));
				successNotification('Domain Saved !');
				setInputVisible(false);
				setDomainInput('');
			} else if (domainData.message !== 'ok') {
				errorNotification(domainData.message);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [domainData]);

	const saveDomain = useCallback(() => {
		if (!domainInput) {
			setInputErrorMessage('Please enter domain address');
			return;
		}
		const newDomain = domainInput.toString().toLowerCase().trim();
		if (!validURL(newDomain) || newDomain.includes('!')) {
			setInputErrorMessage('Invalid domain address');
			return;
		}
		const existing = (whitelistedDomains ?? []).map(d => d?.url);
		if (existing.indexOf(newDomain) !== -1) {
			setInputErrorMessage('This domain already exists.');
			return;
		}
		addDomain(`${API_URL.CLIENT_DETAILS}`, {
			whitelistedDomain: { url: newDomain },
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [domainInput, whitelistedDomains]);

	const confirmDeleteDomain = useCallback(() => {
		const deleteUrl = `${API_URL.CLIENT_DETAILS}/${clientsDetailid}?whitelistedDomain=${deleteID}`;
		removeDomain(deleteUrl).then(res => {
			if (res) {
				if (whitelistedDomains?.length) {
					setKycSettings(prev => ({
						...prev,
						whitelistedDomains: whitelistedDomains.filter(
							d => d._id !== deleteID
						),
					}));
					successNotification('Domain Removed !');
				}
			} else {
				errorNotification(MESSAGE.ERROR);
			}
			setDeleteId('');
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clientsDetailid, deleteID, whitelistedDomains]);

	/* ------------------------------------------------- toggles (v1 logic) */

	const handleWhitelabel = useCallback(
		async (checked: boolean) => {
			await updateWhitelable(`${API_URL.CLIENT_DETAILS}`, {
				whitelabel: checked,
			}).then(res => {
				if (Object.keys(res ?? {}).length !== 0) {
					setKycSettings(prev => ({ ...prev, whitelabel: checked }));
					updateStorage({ whitelabel: checked });
				}
			});
			getPipelineInit();
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const handleShowPricing = useCallback(
		async (checked: boolean) => {
			await updateShowPricing(`${API_URL.CLIENT_DETAILS}`, {
				showPricing: checked,
			}).then(res => {
				if (Object.keys(res ?? {}).length !== 0) {
					setKycSettings(prev => ({ ...prev, showPricing: checked }));
				}
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	/* ----------------------------------------------------- mfa (v1 logic) */

	const handleMultifactorToggle = useCallback(
		async (next: boolean) => {
			if (next) {
				/* v1 opened the modal before the setup call answered, so a failed
				 * call presented as an empty QR screen — a blank to squint at
				 * instead of an error to act on. Gate the modal on the response:
				 * on failure the switch stays off and the reason is a toast. */
				const resp = await post(`${API_URL.MFA_SETUP}`, { type: 'setup' });
				if (!resp || resp.message) {
					errorNotification(
						resp?.message ?? message.SomethingWentWrongMessage
					);
					return;
				}
				setMultifactorModal(true);
			} else {
				setMultifactorModal(false);
				setMfaIsConfirmationModal(true);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[post]
	);

	const closeMfaModal = useCallback(() => {
		setMultifactorModal(false);
		setVerificationCodeValue('');
		setActiveStepName(MFA_ACTIVE_MODAL.RGISTER_MFA);
	}, []);

	const disableMfa = useCallback(() => {
		setMfaIsConfirmationModal(false);
		post(`${API_URL.MFA_SETUP}`, { type: 'disable' });
		setMfaEnabled(false);
		successNotification(MFA_MESSAGES.MFA_DISABLED_SUCCESS);
	}, [post, successNotification]);

	const saveMfa = useCallback(async () => {
		const verifyrResp = await mfa_Verify(`${API_URL.MFA_VERIFY}`, {
			mfaCode: verificationCodeValue,
			userId,
		});
		if (verifyrResp?.verified) {
			setMfaEnabled(true);
			await mfa_back_Codes(`${API_URL.BACK_UP_CODES}`, {
				type: 'generate',
				userId,
			});
			setActiveStepName(MFA_ACTIVE_MODAL.BACK_UP_CODE);
			successNotification(MFA_MESSAGES.MFA_SUCCESS);
		} else {
			errorNotification(MFA_MESSAGES.INVALID_CODE);
		}
	}, [
		errorNotification,
		mfa_Verify,
		mfa_back_Codes,
		successNotification,
		userId,
		verificationCodeValue,
	]);

	const mfaSaveDisabled =
		verificationCodeValue.length <= 4 || mfa_verify_loading || back_up_loading;
	const onRegisterStep = activeStepName === MFA_ACTIVE_MODAL.RGISTER_MFA;
	const mfaHeadings = onRegisterStep
		? MFA_MODAL_HEADING_MESSAGES.genrateCode
		: MFA_MODAL_HEADING_MESSAGES.backupCode;

	const domains = useMemo(
		() => [...(whitelistedDomains ?? [])].reverse(),
		[whitelistedDomains]
	);

	return (
		<div className="sv2-scope sv2 sv2-embed-scope gs-deck">
			<PageHead
				title="General Settings"
				sub={
					<>
						company profile, security and platform behaviour &middot; changes
						apply to every user of this account
					</>
				}
			/>

			<div className="strip estrip">
				<div className="tile">
					<div className="l">White label</div>
					<div className={`n ${whitelabel ? 'g' : 'gy'}`}>
						{whitelabel ? 'ON' : 'OFF'}
					</div>
					<div className="d">your own branding on end-user surfaces</div>
				</div>
				<div className="tile">
					<div className="l">Two-factor auth</div>
					<div className={`n ${mfaEnable ? 'g' : 'gy'}`}>
						{mfaEnable ? 'ON' : 'OFF'}
					</div>
					<div className="d">extra sign-in step for this account</div>
				</div>
				<div className="tile">
					<div className="l">Whitelisted domains</div>
					<div className="n bl">{(whitelistedDomains ?? []).length}</div>
					<div className="d">origins allowed to embed the flows</div>
				</div>
				<div className="tile">
					<div className="l">Timezone</div>
					<div className="n" style={{ fontSize: 15 }}>
						{timezone?.abbreviation || '--'}
					</div>
					<div className="d">{timezone?.name || 'not set'}</div>
				</div>
			</div>

			<div className="gscols">
				<div className="gcol">
					{/* ---- appearance: native rebuild of the same themeModeState ---- */}
					<div className="panel">
						<div className="ph">Appearance</div>
						<div className="swrow">
							<div>
								<div className="swlab">Dashboard theme</div>
								<div className="swsub">
									&ldquo;System&rdquo; follows your device setting
								</div>
							</div>
							<div
								className="seg"
								role="radiogroup"
								aria-label="Theme"
								style={{ marginLeft: 'auto' }}
							>
								{THEME_OPTIONS.map(({ key, label }) => (
									<button
										key={key}
										type="button"
										role="radio"
										aria-checked={themeMode === key}
										onClick={() => applyThemeMode(key)}
									>
										{label}
									</button>
								))}
							</div>
						</div>
					</div>

					{/* ---- company details: v1's own card, mounted unchanged ---- */}
					<div className="panel">
						<div className="ph">Company details</div>
						<div className="embed general-settings-page">
							<div className="company-details-container">
								<CompanyDetails isUserPermissionWrite={canWrite} />
							</div>
						</div>
					</div>

					{parentBusinessId ? (
						<div className="panel">
							<div className="ph">Sub-account</div>
							<div className="embed general-settings-page">
								<div className="company-details-container">
									<SubAccountDetails />
								</div>
							</div>
						</div>
					) : null}

					{/* ---- encryption key: v1's own card ---- */}
					<div className="panel">
						<div className="ph">Encryption key</div>
						<div className="embed general-settings-page flush">
							<EncrptionKeySection />
						</div>
					</div>

					{/* ---- email client: v1's own card ---- */}
					<div className="panel">
						<div className="ph">Email client</div>
						<div className="embed general-settings-page">
							<AddEmailClient isSettingPage={true} />
						</div>
					</div>
				</div>

				<div className="gcol">
					{/* ---- feature toggles: native rebuild, same endpoints ---- */}
					<div className="panel">
						<div className="ph">Platform behaviour</div>
						<div className="swrow">
							<div>
								<div className="swlab">Enable white label</div>
								<div className="swsub">
									serve end-user surfaces under your own branding
								</div>
							</div>
							<Switch
								checked={!!whitelabel}
								onChange={handleWhitelabel}
								disabled={!canWrite}
								label="Enable white label"
								title={
									canWrite
										? undefined
										: 'You are not permitted to toggle white label'
								}
							/>
						</div>
						{whitelabel && canWrite && (
							<div className="embed general-settings-page embed--nested">
								<LoadingAnimation />
							</div>
						)}
						<div className="swrow">
							<div>
								<div className="swlab">Two-factor authentication</div>
								<div className="swsub">
									require an authenticator code at sign-in
								</div>
							</div>
							<Switch
								checked={!!mfaEnable}
								onChange={handleMultifactorToggle}
								disabled={!canWrite}
								label="Two-factor authentication"
								title={
									canWrite
										? undefined
										: 'You are not permitted to toggle Two-factor authentication'
								}
							/>
						</div>
						<div className="swrow">
							<div>
								<div className="swlab">Show pricing</div>
								<div className="swsub">
									display service pricing inside the product
								</div>
							</div>
							<Switch
								checked={!!showPricing}
								onChange={handleShowPricing}
								disabled={!canWrite}
								label="Show pricing"
								title={
									canWrite
										? undefined
										: 'You are not permitted to toggle Show Pricing'
								}
							/>
						</div>
					</div>

					{/* ---- whitelisted domains: native rebuild, same endpoints ---- */}
					<div className="panel">
						<div className="ph">
							Whitelisted domains
							<span className="r">
								{(whitelistedDomains ?? []).length} allowed
							</span>
						</div>
						{canWrite && (
							<div className="domadd">
								{isInputVisible ? (
									<>
										<Field
											label="Domain"
											value={domainInput}
											onChange={v => {
												setDomainInput(v);
												setInputErrorMessage('');
											}}
											placeholder="Type your domain address"
											error={inputErrorMessage}
											autoFocus
										/>
										<div className="domadd__btns">
											<button
												className="btn"
												type="button"
												onClick={() => {
													setInputVisible(false);
													setInputErrorMessage('');
													setDomainInput('');
												}}
											>
												Cancel
											</button>
											<button
												className="btn pri"
												type="button"
												disabled={loading}
												onClick={saveDomain}
											>
												{loading ? 'Saving…' : 'Save'}
											</button>
										</div>
									</>
								) : (
									<button
										className="btn pri"
										type="button"
										onClick={() => setInputVisible(true)}
									>
										Add domain
									</button>
								)}
							</div>
						)}
						{domains.length ? (
							<table className="stbl domtbl">
								<tbody>
									{domains.map(({ url, _id }) => (
										<tr key={_id}>
											<td>
												<a
													className="domlink"
													rel="noreferrer"
													href={url ?? '#'}
													target="_blank"
												>
													{url ?? ''}
												</a>
											</td>
											{canWrite && (
												<td className="accol">
													<button
														className="btn danger dombtn"
														type="button"
														onClick={() => setDeleteId(_id ?? '')}
													>
														Delete
													</button>
												</td>
											)}
										</tr>
									))}
								</tbody>
							</table>
						) : (
							<Blank
								title="No whitelisted domain"
								description="Only whitelisted origins may embed your onboarding flows. Add the first one to start."
							/>
						)}
					</div>

					{/* ---- timezone: v1's own card ---- */}
					{!!login?.isPrimary && (
						<div className="panel">
							<div className="ph">Timezone</div>
							<div className="embed general-settings-page flush">
								<TimezoneSettings />
							</div>
						</div>
					)}
				</div>
			</div>

			{/* ---- delete-domain confirm ---- */}
			<Modal
				open={!!deleteID}
				title={message.TITLE_MESSAGE}
				onClose={() => setDeleteId('')}
				size="narrow"
				sub="Deleting this domain is permanent and cannot be undone."
				footer={
					<>
						<button className="btn" type="button" onClick={() => setDeleteId('')}>
							Cancel
						</button>
						<button
							className="btn danger"
							type="button"
							onClick={confirmDeleteDomain}
						>
							Delete
						</button>
					</>
				}
			/>

			{/* ---- disable-2FA confirm (v1 showed a copy-pasted file-deletion
			     message here; the action is disabling MFA, so say that) ---- */}
			<Modal
				open={mfaConfirmationModal}
				title="Turn off two-factor authentication?"
				onClose={() => setMfaIsConfirmationModal(false)}
				size="narrow"
				sub="Signing in will no longer ask for an authenticator code. You can re-enable it any time."
				footer={
					<>
						<button
							className="btn"
							type="button"
							onClick={() => setMfaIsConfirmationModal(false)}
						>
							Cancel
						</button>
						<button className="btn danger" type="button" onClick={disableMfa}>
							Turn off
						</button>
					</>
				}
			/>

			{/* ---- MFA setup: v1's QR / verify / backup-code bodies, sv2 shell ---- */}
			<Modal
				open={multiFactorModal}
				title={mfaHeadings.heading}
				sub={mfaHeadings.subHeading}
				onClose={closeMfaModal}
				className="sv2-embed-scope mfa-setup-modal"
				footer={
					onRegisterStep ? (
						<>
							<button className="btn" type="button" onClick={closeMfaModal}>
								Cancel
							</button>
							<button
								className="btn pri"
								type="button"
								disabled={mfaSaveDisabled}
								onClick={saveMfa}
							>
								{mfa_verify_loading || back_up_loading ? 'Verifying…' : 'Save'}
							</button>
						</>
					) : (
						<button className="btn" type="button" onClick={closeMfaModal}>
							Done
						</button>
					)
				}
			>
				<div className="embed general-settings-page">
					{onRegisterStep ? (
						<GenrateMultifactorAuth
							mfa_data={mfa_setup_data}
							handleChange={setVerificationCodeValue}
						/>
					) : (
						<BackUpCodes back_up_codes={mfa_back_up_codes} />
					)}
				</div>
			</Modal>
		</div>
	);
};

export default GeneralSettingsDeck;
